import type { LscBillingStatusResponse, LscLeague } from './types';
import { isActivePaidAccountStatus } from './entitlementBillingErrors';
import { isCompedEntitlement } from './billingCopy';

export type WorkspaceBillingBannerMode = 'hidden' | 'linked_success' | 'unbound';

export interface LeagueBillingLinkHint {
  leagueId: string;
  leagueName: string;
  billingStatus: LscBillingStatusResponse | null;
}

export interface PaidUnboundRecoveryState {
  mode: WorkspaceBillingBannerMode;
  /** True when the unbound recovery banner should render. */
  show: boolean;
  linkedLeagueId?: string;
  linkedLeagueName?: string;
  boundLeagueCount: number;
  canQuickBind: boolean;
  quickBindLeagueId?: string;
  settingsLeagueId?: string;
  ownedLeaguesForLink?: Array<{ id: string; name: string }>;
}

export function leagueSubscriptionSettingsHref(leagueId: string): string {
  return `/leagues/${leagueId}/settings#league-subscription`;
}

export function filterOwnedLeagues(
  leagues: LscLeague[],
  ownerUid?: string | null
): LscLeague[] {
  if (!ownerUid) return [];
  return leagues.filter((league) => league.commissioner_uid === ownerUid);
}

export function hasLeagueEntitlement(
  billingStatus: LscBillingStatusResponse | null | undefined
): boolean {
  if (!billingStatus?.entitlement) return false;
  return isCompedEntitlement(billingStatus.entitlement) || Boolean(billingStatus.entitlement);
}

export function resolveLinkedSubscriptionLeague(
  accountBilling: LscBillingStatusResponse | null | undefined,
  leagueBillingHints?: LeagueBillingLinkHint[]
): { leagueId: string; leagueName: string } | null {
  const bound = accountBilling?.bound_leagues || [];
  if (bound.length > 0) {
    const first = bound[0];
    return {
      leagueId: first.league_id,
      leagueName: first.name || first.league_id,
    };
  }

  if (hasLeagueEntitlement(accountBilling)) {
    return null;
  }

  for (const hint of leagueBillingHints || []) {
    if (hasLeagueEntitlement(hint.billingStatus)) {
      return {
        leagueId: hint.leagueId,
        leagueName: hint.leagueName,
      };
    }
  }

  return null;
}

export function resolvePaidUnboundRecovery(
  billingStatus: LscBillingStatusResponse | null | undefined,
  options?: {
    /** Owned league ids where bind is allowed (typically commissioner-owned leagues). */
    ownedLeagueIds?: string[];
    ownedLeagues?: Array<{ id: string; name: string }>;
    leagueBillingHints?: LeagueBillingLinkHint[];
  }
): PaidUnboundRecoveryState {
  const hidden: PaidUnboundRecoveryState = {
    mode: 'hidden',
    show: false,
    boundLeagueCount: 0,
    canQuickBind: false,
  };

  if (!billingStatus?.billing_model_enabled) {
    return hidden;
  }

  const linked = resolveLinkedSubscriptionLeague(
    billingStatus,
    options?.leagueBillingHints
  );
  if (linked) {
    return {
      mode: 'linked_success',
      show: false,
      linkedLeagueId: linked.leagueId,
      linkedLeagueName: linked.leagueName,
      boundLeagueCount: billingStatus.bound_leagues?.length ?? 1,
      canQuickBind: false,
      settingsLeagueId: linked.leagueId,
    };
  }

  if (billingStatus.entitlement || isCompedEntitlement(billingStatus.entitlement)) {
    return hidden;
  }

  if (!isActivePaidAccountStatus(billingStatus)) {
    return hidden;
  }

  const ownedLeagues =
    options?.ownedLeagues ||
    (options?.ownedLeagueIds || []).map((id) => ({ id, name: id }));
  const boundLeagueCount = billingStatus.bound_leagues?.length ?? 0;
  const canQuickBind = ownedLeagues.length === 1;
  const settingsLeagueId =
    ownedLeagues.length === 1
      ? ownedLeagues[0].id
      : ownedLeagues[0]?.id;

  return {
    mode: 'unbound',
    show: true,
    boundLeagueCount,
    canQuickBind,
    quickBindLeagueId: canQuickBind ? ownedLeagues[0]?.id : undefined,
    settingsLeagueId,
    ownedLeaguesForLink: ownedLeagues.length > 1 ? ownedLeagues : undefined,
  };
}
